import {
  Banner,
  Box,
  Button,
  Checkbox,
  ConfirmCancelDialog,
  Select,
  Spacer,
  Text,
  Tooltip,
  useDialogState,
} from '@color/continuum';
import { type ReactNode, useMemo, useState } from 'react';

import { isInternalConfirmation } from '../confirmationWorkflow';
import {
  REQUEST_PRIMER_BLOCKER_MESSAGE,
  isRequestBlockedByPrimers,
  requiresPrimers,
} from '../DetailView/PrimersSection/lifecycle';
import {
  VARIANT_CONFIRMATION_METHODS,
  VARIANT_CONFIRMATION_VENDORS,
  VariantConfirmationMethod,
} from '../methodVendorOptions';
import {
  SelectedConfirmationsTable,
  type SelectedConfirmationsTableExtensionColumn,
} from '../SelectedConfirmationsTable';
import type { VariantConfirmation } from '../types';
import { VariantConfirmationState } from '../types';

import { buildRequestConfirmationDraft, ConfirmationDraft } from './draftUtils';
import { useSubmitVariantConfirmationRequests } from './useSubmitVariantConfirmationRequests';

export const REQUEST_METHOD_VENDOR_MISSING_MESSAGE =
  'Set method and vendor in Sendout Info before requesting.';

export function isVariantConfirmationStateRequestable(state: VariantConfirmationState): boolean {
  return state === VariantConfirmationState.NEW;
}

function isMethodVendorMissing(confirmation: VariantConfirmation): boolean {
  return confirmation.method.length === 0 || confirmation.vendor.length === 0;
}

export interface RequestConfirmationDialogProps {
  confirmations: VariantConfirmation[];
  onClose: () => void;
  onSuccess: () => void;
}

function getRequestExtensionColumns(options: {
  drafts: ConfirmationDraft[];
  showNewPrimers: boolean;
  onChange: (id: number, field: keyof ConfirmationDraft, value: string | boolean) => void;
}): SelectedConfirmationsTableExtensionColumn[] {
  const { drafts, showNewPrimers, onChange } = options;

  const getDraft = (confirmation: VariantConfirmation): ConfirmationDraft => {
    const draft = drafts.find((d) => d.id === confirmation.id);
    if (draft == null) {
      throw new Error(`No draft for confirmation id ${confirmation.id}`);
    }
    return draft;
  };

  const columns: SelectedConfirmationsTableExtensionColumn[] = [
    {
      key: 'method',
      header: 'Method',
      renderCell: (confirmation) => {
        const draft = getDraft(confirmation);
        return (
          <Select
            native
            size="small"
            value={draft.method}
            onChange={(e) => onChange(draft.id, 'method', e.target.value as string)}
          >
            <option value="" disabled>
              Select method
            </option>
            {VARIANT_CONFIRMATION_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        );
      },
    },
    {
      key: 'vendor',
      header: 'Vendor',
      renderCell: (confirmation) => {
        const draft = getDraft(confirmation);
        return (
          <Select
            native
            size="small"
            value={draft.vendor}
            onChange={(e) => onChange(draft.id, 'vendor', e.target.value as string)}
          >
            <option value="" disabled>
              Select vendor
            </option>
            {VARIANT_CONFIRMATION_VENDORS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        );
      },
    },
    {
      key: 'double-dna',
      header: 'Double DNA',
      renderCell: (confirmation) => {
        const draft = getDraft(confirmation);
        const methodIsSanger = draft.method === VariantConfirmationMethod.Sanger;
        return (
          <>
            {methodIsSanger && (
              <Checkbox
                label=""
                value={draft.use_double_dna}
                onChange={(e) => onChange(draft.id, 'use_double_dna', e.target.checked)}
              />
            )}
          </>
        );
      },
    },
  ];

  if (showNewPrimers) {
    columns.push({
      key: 'new-primers',
      header: 'New Primers',
      renderCell: (confirmation) => {
        const draft = getDraft(confirmation);
        const methodIsSanger = draft.method === VariantConfirmationMethod.Sanger;
        return (
          <>
            {methodIsSanger && draft.needs_new_primers === null && (
              <Text variant="body2" color="error">
                UNDEFINED
              </Text>
            )}
            {methodIsSanger && draft.needs_new_primers !== null && (
              <Text variant="body2">{draft.needs_new_primers ? 'Yes' : 'No'}</Text>
            )}
          </>
        );
      },
    });
  }

  return columns;
}

/**
 * Batch request dialog for ListView. Lets the user set method/vendor
 * (and use_double_dna) for each selected confirmation before submitting.
 */
export const RequestConfirmationDialog = ({
  confirmations,
  onClose,
  onSuccess,
}: RequestConfirmationDialogProps) => {
  const [drafts, setDrafts] = useState<ConfirmationDraft[]>(() =>
    confirmations.map(buildRequestConfirmationDraft)
  );

  const { submitDrafts, isLoading } = useSubmitVariantConfirmationRequests({
    onSuccessAfterSubmit: onSuccess,
  });

  const handleChange = (id: number, field: keyof ConfirmationDraft, value: string | boolean) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
  };

  const handleSubmit = async () => {
    await submitDrafts(drafts);
  };

  const cnvDrafts = drafts.filter((d) => d.is_cnv);
  const nonCnvDrafts = drafts.filter((d) => !d.is_cnv);

  // Mirror `mark_as_queued`'s primer gate so the button is visibly disabled
  // with an explanation, not silently rejected by the backend.
  const blockedDrafts = useMemo(() => drafts.filter((d) => isRequestBlockedByPrimers(d)), [drafts]);
  const hasPrimerBlockers = blockedDrafts.length > 0;
  const submitDisabled = isLoading || hasPrimerBlockers;
  const submitTooltip = hasPrimerBlockers ? REQUEST_PRIMER_BLOCKER_MESSAGE : '';

  const title = `Request ${confirmations.length} variant confirmation${confirmations.length !== 1 ? 's' : ''}`;

  return (
    <ConfirmCancelDialog
      title={title}
      confirmButtonText="Request"
      confirmButtonProps={{ disabled: submitDisabled, title: submitTooltip }}
      onAccept={handleSubmit}
      onCancel={onClose}
      dialogIsOpen
      fullWidth
      maxWidth="lg"
    >
      {hasPrimerBlockers && (
        <Banner severity="warning" variant="in-component" sx={{ marginBottom: 2 }}>
          {`${REQUEST_PRIMER_BLOCKER_MESSAGE} Affected: ${blockedDrafts
            .map((d) => d.confirmation.accession_number)
            .join(', ')}.`}
        </Banner>
      )}
      {cnvDrafts.length > 0 && (
        <Box sx={{ marginBottom: 2 }}>
          <Text variant="subtitle1">Structural Variants</Text>
          <SelectedConfirmationsTable
            confirmations={cnvDrafts.map((d) => d.confirmation)}
            showSampleColumn={false}
            extensionVerticalAlign="middle"
            extensionColumns={getRequestExtensionColumns({
              drafts: cnvDrafts,
              showNewPrimers: true,
              onChange: handleChange,
            })}
          />
        </Box>
      )}
      {nonCnvDrafts.length > 0 && (
        <Box sx={{ marginBottom: 2 }}>
          <Text variant="subtitle1">Small Variants</Text>
          <SelectedConfirmationsTable
            confirmations={nonCnvDrafts.map((d) => d.confirmation)}
            showSampleColumn={false}
            extensionVerticalAlign="middle"
            extensionColumns={getRequestExtensionColumns({
              drafts: nonCnvDrafts,
              showNewPrimers: false,
              onChange: handleChange,
            })}
          />
        </Box>
      )}
    </ConfirmCancelDialog>
  );
};

interface SingleRequestConfirmDialogProps {
  confirmation: VariantConfirmation;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Detail-view request dialog. Method/vendor are already set (the Request
 * button is gated behind that), so this is just a final "are you sure"
 * confirmation that surfaces the configured values.
 */
const SingleRequestConfirmDialog = ({
  confirmation,
  onClose,
  onSuccess,
}: SingleRequestConfirmDialogProps) => {
  const { submitDrafts, isLoading } = useSubmitVariantConfirmationRequests({
    onSuccessAfterSubmit: onSuccess,
  });

  const handleSubmit = async () => {
    await submitDrafts([buildRequestConfirmationDraft(confirmation)]);
  };

  const methodIsSanger = confirmation.method === VariantConfirmationMethod.Sanger;

  return (
    <ConfirmCancelDialog
      title="Request confirmation"
      confirmButtonText="Request"
      confirmButtonProps={{ disabled: isLoading }}
      onAccept={handleSubmit}
      onCancel={onClose}
      dialogIsOpen
      fullWidth
      maxWidth="xs"
    >
      <Text>Send this variant confirmation request to the vendor?</Text>
      <Spacer variant="medium" />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 1 }}>
        <Text variant="body2" sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
          Method
        </Text>
        <Text variant="body2">{confirmation.method}</Text>
        <Text variant="body2" sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
          Vendor
        </Text>
        <Text variant="body2">{confirmation.vendor}</Text>
        {methodIsSanger && (
          <>
            <Text variant="body2" sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
              Use double DNA
            </Text>
            <Text variant="body2">{confirmation.use_double_dna ? 'Yes' : 'No'}</Text>
          </>
        )}
      </Box>
    </ConfirmCancelDialog>
  );
};

export function useVariantConfirmationRequestAction(
  confirmation: VariantConfirmation,
  options: { onSuccess: () => void }
): { button: ReactNode | null } {
  const { dialogIsOpen, openDialog, closeDialog } = useDialogState();

  if (!isVariantConfirmationStateRequestable(confirmation.state)) {
    return { button: null };
  }

  // Internal confirmations (Color vendor + NGS method) reconfirm in-house and
  // do not go through the external Request workflow; they expose their own
  // actions instead.
  if (isInternalConfirmation(confirmation)) {
    return { button: null };
  }

  // This confirmation is persisted, so trust the server-computed gate rather
  // than re-deriving it (the draft mirror is only for the in-flight dialog).
  const blockedByPrimers = requiresPrimers(confirmation) && confirmation.needs_new_primers === null;
  const methodVendorMissing = isMethodVendorMissing(confirmation);

  // Primer gate is the more specific failure (method is set, but the primer
  // workflow isn't resolved), so prefer it when both apply.
  const disabledReason = (() => {
    if (blockedByPrimers) return REQUEST_PRIMER_BLOCKER_MESSAGE;
    if (methodVendorMissing) return REQUEST_METHOD_VENDOR_MISSING_MESSAGE;
    return '';
  })();
  const disabled = disabledReason.length > 0;

  const button = (
    <>
      <Tooltip title={disabledReason}>
        <Box component="span">
          <Button variant="contained" size="small" disabled={disabled} onClick={openDialog}>
            Request
          </Button>
        </Box>
      </Tooltip>
      {dialogIsOpen && (
        <SingleRequestConfirmDialog
          confirmation={confirmation}
          onClose={closeDialog}
          onSuccess={() => {
            closeDialog();
            options.onSuccess();
          }}
        />
      )}
    </>
  );

  return { button };
}
